import * as React from 'react'
import { flatMap } from 'lodash'
import { Check, ChevronUp, Minus, Search, X } from 'lucide-react'

import {
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Spinner,
} from '@/components/ui'
import { useProjectInfinite } from '@/features/project/hooks'
import { useDebounce } from '@/hooks/useDebounce'
import { cn } from '@/utils/common'
import { MIN_SEARCH_LENGTH, SEARCH_DEBOUNCE_DELAY_MS } from '@/utils/constants'

import { useInfinityLoadMore } from '../../hooks/useInfinityLoadMore'
import { ResponseProjectDropdown } from '../../types'
import { groupProjectsByCompany } from '../../utils'

type TProjectAccessProps = {
  selectedProjects?: string[]
  setSelectedProjects?: (projects: string[]) => void
  containerClassName?: string
}

const ProjectAccess: React.FC<TProjectAccessProps> = ({
  selectedProjects = [],
  setSelectedProjects,
  containerClassName,
}) => {
  const [searchText, setSearchText] = React.useState<string>('')

  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_DELAY_MS)

  const searchQuery = React.useMemo(() => {
    const trimmed = debouncedSearchText?.trim() || ''
    return trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : ''
  }, [debouncedSearchText])

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isLoading } =
    useProjectInfinite({ search: searchQuery })

  const { scrollContainerRef, loadMoreRef } = useInfinityLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  })

  const companies = React.useMemo(() => {
    const result =
      flatMap(
        data?.pages,
        (page) => page?.data as unknown as ResponseProjectDropdown[]
      ) || []

    return groupProjectsByCompany(result)
  }, [data])

  const handleCompanyToggle = React.useCallback(
    (companyId: string, isChecked: boolean) => {
      let projectIds: string[] = []

      const company = companies.find((c) => c.id === companyId)
      if (company) projectIds = company.projects?.map((p) => p.id) || []

      const newProjects = isChecked
        ? [...selectedProjects, ...projectIds]
        : selectedProjects.filter((p) => !projectIds.includes(p))

      setSelectedProjects?.(newProjects)
    },
    [companies, selectedProjects, setSelectedProjects]
  )

  const handleProjectToggle = React.useCallback(
    (projectId: string) => {
      const isSelected = selectedProjects.includes(projectId)

      const newProjects = isSelected
        ? selectedProjects.filter((p) => p !== projectId)
        : [...selectedProjects, projectId]

      setSelectedProjects?.(newProjects)
    },
    [selectedProjects, setSelectedProjects]
  )

  const getCompanySelectedState = React.useCallback(
    (companyId: string): boolean => {
      const company = companies.find((c) => c.id === companyId)

      if (!company) return false
      const projectIds = company.projects?.map((p) => p.id) || []

      return projectIds.every((id) => selectedProjects.includes(id))
    },
    [companies, selectedProjects]
  )

  return (
    <div className="space-y-3">
      <div className="relative w-full">
        <Input
          type="text"
          placeholder="Search by company or project name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="w-full"
          autoComplete="off"
          leadingIcon={<Search size={20} className="text-gray-400" />}
          trailingIcon={
            searchText ? (
              <X className="w-3.5 h-3.5 cursor-pointer" />
            ) : undefined
          }
          onTrailingIconClick={searchText ? () => setSearchText('') : undefined}
        />
      </div>
      <div className="flex flex-col rounded-xl border border-solid border-secondary">
        <div className="px-5 py-3 text-xs text-tertiary-600 font-semibold uppercase border-b border-secondary">
          Project Access
        </div>
        <div
          ref={scrollContainerRef}
          className={cn(
            'px-2 py-3 max-h-120 overflow-y-auto overflow-x-hidden',
            containerClassName
          )}
        >
          <div className="relative h-fit space-y-0 5">
            {isLoading ? (
              <div className="flex items-center justify-center py-2 h-40">
                <Spinner className="size-4 text-brand-600" />
              </div>
            ) : (
              companies.map((company) => {
                const isSelectAll = getCompanySelectedState(company.id)
                const projectIds = company.projects?.map((p) => p.id) || []

                if (!projectIds?.length) return null

                const selectedCount = projectIds.filter((id) =>
                  selectedProjects.includes(id)
                ).length
                const totalCount = projectIds.length

                return (
                  <Collapsible
                    defaultOpen={false}
                    key={company.id}
                    className="relative z-10"
                  >
                    <CollapsibleTrigger asChild className="group">
                      <div className="flex items-center justify-between">
                        <button className="flex items-center gap-2 bg-transparent cursor-pointer p-2">
                          <div className="size-5 flex-shrink-0">
                            <ChevronUp
                              size={20}
                              className={cn(
                                'size-5 transition-transform duration-200 text-foreground-fg-quaternary-400',
                                'group-data-[state=open]:rotate-180'
                              )}
                            />
                          </div>
                          <button
                            type="button"
                            className="flex gap-2 cursor-pointer items-center p-1 bg-transparent border-none overflow-hidden"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleCompanyToggle(company.id, !isSelectAll)
                            }}
                            aria-label={
                              isSelectAll ? 'Deselect all' : 'Select all'
                            }
                            aria-pressed={isSelectAll}
                          >
                            <div
                              className={cn(
                                'size-5 rounded-xs flex items-center justify-center',
                                'border border-solid border-primary',
                                selectedCount
                                  ? 'bg-brand-solid border-transparent'
                                  : 'bg-transparent'
                              )}
                            >
                              {selectedCount === totalCount ? (
                                <Check size={12} className="text-white" />
                              ) : (
                                <Minus size={12} className="text-white" />
                              )}
                            </div>
                            <div className="text-sm font-semibold text-primary-900 break-words max-w-[calc(100%-28px)]">
                              {company.name}
                            </div>
                          </button>
                        </button>
                        {selectedCount > 0 ? (
                          <div className="px-sm py-xxs bg-gray-50 border border-solid border-gray-200 rounded-sm text-xs text-gray-700 font-medium">
                            {selectedCount}
                          </div>
                        ) : null}
                      </div>
                    </CollapsibleTrigger>

                    <CollapsibleContent className="pl-16 pt-1">
                      {company.projects?.map((project) => {
                        if (!project) return null

                        return (
                          <div
                            key={project.id}
                            className="flex items-center gap-2 p-2"
                          >
                            <Checkbox
                              checked={selectedProjects.includes(project.id)}
                              onChange={() => handleProjectToggle(project.id)}
                            />
                            <div className="text-sm font-medium text-primary-900 break-words max-w-[calc(100%-28px)]">
                              {project.name}
                            </div>
                          </div>
                        )
                      })}
                    </CollapsibleContent>
                  </Collapsible>
                )
              })
            )}
            {isFetchingNextPage ? (
              <div className="flex items-center justify-center py-2">
                <Spinner className="size-4 text-brand-600" />
              </div>
            ) : null}
            <div
              ref={loadMoreRef}
              className="absolute bottom-20 w-full h-10 z-0"
            />
          </div>
        </div>
      </div>
      <div className="pb-3 pl-4 border-b border-secondary">
        <span className="text-sm font-medium text-tertiary-600">
          <b>{selectedProjects.length}</b> project
          {selectedProjects.length > 1 ? 's' : ''} selected
        </span>
      </div>
    </div>
  )
}

const MemoizedProjectAccess = React.memo(ProjectAccess)
MemoizedProjectAccess.displayName = 'ProjectAccess'

export { MemoizedProjectAccess as ProjectAccess }
